import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  message,
  Table,
  DatePicker,
  Space,
  Statistic,
  Row,
  Col,
  Select,
  Alert,
  Typography,
  Divider,
  Collapse,
  Progress,
  Tag,
  Modal
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ShopOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { ref, get, onValue, off } from 'firebase/database';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useFirebase } from '../FirebaseContext';
import dayjs from 'dayjs';
import './ExportXMLComarch.css';

const { Option } = Select;
const { Title, Text } = Typography;

const ExportXMLComarch = () => {
  const { currentFirebase, currentProject } = useFirebase();
  const db = currentFirebase.database;
  const firestore = currentFirebase.firestore;

  // Используем функции из текущего проекта
  const functionsRegion = 'us-central1';
  const functions = getFunctions(currentFirebase.app, functionsRegion);

  // Состояния
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    accepted: 0,
    rejected: 0,
    inProgress: 0,
    contractors: 0
  });

  // Новое состояние для выбора источника данных
  const [dataSource, setDataSource] = useState('realtime'); // 'realtime' или 'archive'

  // Используем отдельные состояния для начальной и конечной даты
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));
  const [filterStatus, setFilterStatus] = useState('zaakceptowany'); // Domyślnie tylko zaakceptowane
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // Wybrane faktury do eksportu

  // Состояния для задач экспорта
  const [exportTasks, setExportTasks] = useState([]);
  const [showTasksModal, setShowTasksModal] = useState(false);

  // Загрузка данных при монтировании компонента (один раз)
  useEffect(() => {
    loadInvoices();
    loadExportTasks();
  }, []); // Пустой массив зависимостей - загрузка только при монтировании

  // Подписка на обновления задач
  useEffect(() => {
    const tasksRef = ref(db, 'exportTasks');
    const unsubscribe = onValue(tasksRef, (snapshot) => {
      const tasks = [];
      snapshot.forEach((childSnapshot) => {
        tasks.push({
          ...childSnapshot.val(),
          taskId: childSnapshot.key
        });
      });
      // Сортируем по дате создания (новые сверху)
      tasks.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setExportTasks(tasks);
    });

    return () => off(tasksRef, 'value', unsubscribe);
  }, [db]);

  // Funkcja do generowania UUID v4 z NIP
  const generateUUIDFromNIP = (nip) => {
    if (!nip) return `unknown-${Math.random().toString(36).substr(2, 9)}`;
    // Proste generowanie UUID-podobnego ID na podstawie NIP
    const hash = nip.split('').reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);
    return `${hash.toString(16).padStart(8, '0')}-${nip.substr(0, 4)}-${nip.substr(4, 4)}-${nip.substr(8)}`;
  };

  // Funkcja do normalizacji NIP (usuwanie myślników, spacji)
  const normalizeNIP = (nip) => {
    if (!nip) return '';
    return nip.replace(/[^0-9]/g, '');
  };

  // Загрузка фактур
  const loadInvoices = async () => {
    setLoading(true);

    try {
      const snapshot = await get(ref(db, '/drivers'));
      const drivers = snapshot.val();
      let allInvoices = [];
      let contractorsMap = new Map(); // Kontrahenci unikalny po NIP
      let stats = {
        total: 0,
        accepted: 0,
        rejected: 0,
        inProgress: 0,
        contractors: 0
      };

      const startTimestamp = dayjs(startDate).startOf('day').valueOf();
      const endTimestamp = dayjs(endDate).endOf('day').valueOf();

      for (let driverId in drivers) {
        const driverData = drivers[driverId];

        if (driverData.invoices) {
          for (let invoiceId in driverData.invoices) {
            const invoice = driverData.invoices[invoiceId];

            // Проверка наличия invoiceDate или purchaseDate
            const invoiceDateStr = invoice.invoiceDate || invoice.purchaseDate;
            if (!invoiceDateStr) {
              console.warn(`Invoice ${invoiceId} missing invoiceDate/purchaseDate`);
              continue;
            }

            // Парсим дату фактуры в различных форматах
            let invoiceDateMoment = dayjs(invoiceDateStr, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD.MM.YYYY'], true);

            // Если дата невалидна, пропускаем фактуру
            if (!invoiceDateMoment.isValid()) {
              console.warn(`Invoice ${invoiceId} has invalid date: ${invoiceDateStr}`);
              continue;
            }

            const invoiceTimestamp = invoiceDateMoment.valueOf();

            // Фильтрация по дате фактуры
            if (invoiceTimestamp < startTimestamp || invoiceTimestamp > endTimestamp) {
              continue;
            }

            // Фильтрация по статусу
            if (filterStatus !== 'all' && invoice.status !== filterStatus) {
              continue;
            }

            // Проверка наличия обязательных полей
            if (!invoice.nipseller) {
              console.warn(`Invoice ${invoiceId} missing nipseller`);
              continue;
            }

            const invoiceData = {
              ...invoice,
              driverId,
              invoiceId,
              key: `${driverId}_${invoiceId}`,
              driverName: driverData.name || driverId,
              driverAkronim: driverData.akronim || '00000',
              date: invoiceDateMoment.format('YYYY-MM-DD'),
              displayDate: invoiceDateMoment.format('DD.MM.YYYY'),
              timestamp: invoiceTimestamp // Сохраняем timestamp для сортировки
            };

            allInvoices.push(invoiceData);
            stats.total++;

            // Подсчет статистики
            if (invoice.status === 'zaakceptowany') stats.accepted++;
            else if (invoice.status === 'odrzucony') stats.rejected++;
            else if (invoice.status === 'W trakcie weryfikacji') stats.inProgress++;

            // Zbieranie kontrahentów (unikalny po NIP)
            const nip = normalizeNIP(invoice.nipseller);
            if (nip && !contractorsMap.has(nip)) {
              contractorsMap.set(nip, {
                nip: nip,
                id: generateUUIDFromNIP(nip),
                akronim: `KNT-${nip.substr(-4)}`,
                nazwa: invoice.vendorName || nip,
                invoicesCount: 1
              });
            } else if (nip && contractorsMap.has(nip)) {
              const contractor = contractorsMap.get(nip);
              contractor.invoicesCount++;
            }
          }
        }
      }

      // Сортировка по дате
      allInvoices.sort((a, b) => b.timestamp - a.timestamp);

      setInvoices(allInvoices);
      setContractors(Array.from(contractorsMap.values()));
      stats.contractors = contractorsMap.size;
      setStatistics(stats);

      // Automatycznie zaznacz wszystkie faktury
      setSelectedRowKeys(allInvoices.map(inv => inv.key));

      message.success(`Załadowano ${allInvoices.length} faktur i ${contractorsMap.size} kontrahentów`);
    } catch (error) {
      console.error('Error loading invoices:', error);
      message.error('Błąd podczas ładowania faktur');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка архивных фактур из Firestore
  const loadArchiveInvoices = async () => {
    setLoading(true);

    try {
      let allInvoices = [];
      let contractorsMap = new Map();
      let stats = {
        total: 0,
        accepted: 0,
        rejected: 0,
        inProgress: 0,
        contractors: 0
      };

      const startTimestamp = dayjs(startDate).startOf('day').valueOf();
      const endTimestamp = dayjs(endDate).endOf('day').valueOf();

      // Получаем все документы из коллекции /finance
      const financeSnapshot = await getDocs(collection(firestore, 'finance'));

      for (const financeDoc of financeSnapshot.docs) {
        const formaId = financeDoc.id;

        // Получаем подколлекцию wydatki
        const wydatkiSnapshot = await getDocs(collection(firestore, `finance/${formaId}/wydatki`));

        for (const wydatekDoc of wydatkiSnapshot.docs) {
          const invoice = wydatekDoc.data();

          // Парсим дату из purchaseDate
          const purchaseDateStr = invoice.purchaseDate;
          if (!purchaseDateStr) {
            console.warn(`Archive invoice ${wydatekDoc.id} missing purchaseDate`);
            continue;
          }

          // Извлекаем дату из формата "(format daty: YYYY-MM-DD): 2024-06-17"
          const dateMatch = purchaseDateStr.match(/(\d{4}-\d{2}-\d{2})/);
          if (!dateMatch) {
            console.warn(`Archive invoice ${wydatekDoc.id} has invalid date format: ${purchaseDateStr}`);
            continue;
          }

          const invoiceDateMoment = dayjs(dateMatch[1], 'YYYY-MM-DD');
          if (!invoiceDateMoment.isValid()) {
            console.warn(`Archive invoice ${wydatekDoc.id} has invalid date: ${dateMatch[1]}`);
            continue;
          }

          const invoiceTimestamp = invoiceDateMoment.valueOf();

          // Фильтрация по дате
          if (invoiceTimestamp < startTimestamp || invoiceTimestamp > endTimestamp) {
            continue;
          }

          // Архивные фактуры считаем все как "zaakceptowany"
          if (filterStatus !== 'all' && filterStatus !== 'zaakceptowany') {
            continue;
          }

          // Проверка NIP
          if (!invoice.nip) {
            console.warn(`Archive invoice ${wydatekDoc.id} missing nip`);
            continue;
          }

          const invoiceData = {
            ...invoice,
            driverId: formaId,
            invoiceId: wydatekDoc.id,
            key: `archive_${formaId}_${wydatekDoc.id}`,
            driverName: formaId,
            driverAkronim: '00000',
            date: invoiceDateMoment.format('YYYY-MM-DD'),
            displayDate: invoiceDateMoment.format('DD.MM.YYYY'),
            timestamp: invoiceTimestamp,
            status: 'zaakceptowany', // Архивные всегда zaakceptowany
            nipseller: invoice.nip,
            vendorName: Array.isArray(invoice.companyName) ? invoice.companyName[0] : invoice.companyName,
            type: 'Faktura archiwalna',
            isArchive: true // Помечаем как архивную
          };

          allInvoices.push(invoiceData);
          stats.total++;
          stats.accepted++;

          // Zbieranie kontrahentów
          const nip = normalizeNIP(invoice.nip);
          if (nip && !contractorsMap.has(nip)) {
            const vendorName = Array.isArray(invoice.companyName) ? invoice.companyName[0] : invoice.companyName;
            contractorsMap.set(nip, {
              nip: nip,
              id: generateUUIDFromNIP(nip),
              akronim: `KNT-${nip.substr(-4)}`,
              nazwa: vendorName || nip,
              invoicesCount: 1
            });
          } else if (nip && contractorsMap.has(nip)) {
            const contractor = contractorsMap.get(nip);
            contractor.invoicesCount++;
          }
        }
      }

      // Сортировка по дате
      allInvoices.sort((a, b) => b.timestamp - a.timestamp);

      setInvoices(allInvoices);
      setContractors(Array.from(contractorsMap.values()));
      stats.contractors = contractorsMap.size;
      setStatistics(stats);

      // Automatycznie zaznacz wszystkie faktury
      setSelectedRowKeys(allInvoices.map(inv => inv.key));

      message.success(`Załadowano ${allInvoices.length} faktur archiwalnych i ${contractorsMap.size} kontrahentów`);
    } catch (error) {
      console.error('Error loading archive invoices:', error);
      message.error('Błąd podczas ładowania faktur archiwalnych');
    } finally {
      setLoading(false);
    }
  };

  // Funkcja escape dla XML CDATA
  const escapeXML = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  // Funkcja do określenia kategorii zakupu
  const determineCategory = (invoice) => {
    if (invoice.fuelType || invoice.type === 'Faktura' || invoice.type === 'Paragon') {
      return 'PALIWO';
    }
    return 'USLUGI';
  };

  // Generowanie XML - sekcja KONTRAHENCI
  const generateContractorsXML = (selectedInvoices) => {
    // Filtruj kontrahentów tylko dla wybranych faktur
    const selectedNIPs = new Set(selectedInvoices.map(inv => normalizeNIP(inv.nipseller)));
    const selectedContractors = contractors.filter(c => selectedNIPs.has(c.nip));

    let xml = `    <KONTRAHENCI>
        <WERSJA>2.00</WERSJA>
        <BAZA_ZRD_ID>SPARI</BAZA_ZRD_ID>
        <BAZA_DOC_ID></BAZA_DOC_ID>

`;

    selectedContractors.forEach(contractor => {
      xml += `        <KONTRAHENT>
            <ID_ZRODLA>${contractor.id}</ID_ZRODLA>
            <AKRONIM>${escapeXML(contractor.akronim)}</AKRONIM>

            <RODZAJ>dostawca</RODZAJ>
            <EKSPORT>krajowy</EKSPORT>
            <FINALNY>Nie</FINALNY>
            <PLATNIK_VAT>Tak</PLATNIK_VAT>
            <NIEAKTYWNY>Nie</NIEAKTYWNY>
            <METODA_KASOWA>Nie</METODA_KASOWA>

            <KAT_ZAKUP>PALIWO</KAT_ZAKUP>
            <FORMA_PLATNOSCI>Karta flotowa</FORMA_PLATNOSCI>
            <TERMIN>14</TERMIN>

            <ADRESY>
                <ADRES>
                    <STATUS>aktualny</STATUS>

                    <NAZWA1><![CDATA[${contractor.nazwa}]]></NAZWA1>
                    <NIP_KRAJ>PL</NIP_KRAJ>
                    <NIP>${contractor.nip}</NIP>

                    <KRAJ><![CDATA[Polska]]></KRAJ>
                </ADRES>
            </ADRESY>

            <ATRYBUTY>
                <ATRYBUT>
                    <KOD_ATR>SPARIO_INVOICES_COUNT</KOD_ATR>
                    <WARTOSC>${contractor.invoicesCount}</WARTOSC>
                </ATRYBUT>
                <ATRYBUT>
                    <KOD_ATR>EXPORT_DATE</KOD_ATR>
                    <WARTOSC>${dayjs().format('YYYY-MM-DD HH:mm:ss')}</WARTOSC>
                </ATRYBUT>
            </ATRYBUTY>
        </KONTRAHENT>

`;
    });

    xml += `    </KONTRAHENCI>

`;
    return xml;
  };

  // Funkcja do kompresji i konwersji obrazu URL do Base64
  const imageUrlToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`Failed to fetch image: ${url}, status: ${response.status}`);
        return null;
      }
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            // Ustaw maksymalną szerokość/wysokość dla kompresji
            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = 1920;
            const QUALITY = 0.7; // 70% jakości JPEG

            let width = img.width;
            let height = img.height;

            // Skaluj obraz jeśli jest za duży
            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
              const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
              width = Math.floor(width * ratio);
              height = Math.floor(height * ratio);
            }

            // Utwórz canvas i narysuj przeskalowany obraz
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Konwertuj do JPEG z kompresją
            const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);

            // Sprawdź czy wynik zawiera przecinek
            const commaIndex = dataUrl.indexOf(',');
            if (commaIndex === -1) {
              console.error('Invalid data URL format - no comma found');
              resolve(null);
              return;
            }

            // Wyciągnij czysty base64 (wszystko po przecinku)
            const base64 = dataUrl.substring(commaIndex + 1);

            if (!base64 || base64.length === 0) {
              console.error('Failed to convert image to Base64, empty result');
              resolve(null);
            } else {
              // Usuń wszystkie białe znaki, znaki nowej linii itp.
              const cleanBase64 = base64.replace(/\s/g, '');
              console.log('Compressed base64 length:', cleanBase64.length, 'Original size approx:', blob.size);
              resolve(cleanBase64);
            }
          } catch (err) {
            console.error('Error processing image:', err);
            resolve(null);
          }
        };

        img.onerror = (error) => {
          console.error('Image load error:', error);
          reject(error);
        };

        img.src = URL.createObjectURL(blob);
      });
    } catch (error) {
      console.error(`Error converting image to base64 (${url}):`, error);
      return null;
    }
  };

  // Generowanie XML - sekcja REJESTRY_ZAKUPU_VAT
  const generateInvoicesXML = async (selectedInvoices) => {
    let xml = `    <REJESTRY_ZAKUPU_VAT>
        <WERSJA>2.00</WERSJA>
        <BAZA_ZRD_ID>SPARI</BAZA_ZRD_ID>
        <BAZA_DOC_ID></BAZA_DOC_ID>

`;

    for (let index = 0; index < selectedInvoices.length; index++) {
      const invoice = selectedInvoices[index];
      const nip = normalizeNIP(invoice.nipseller);
      const contractor = contractors.find(c => c.nip === nip);
      const category = determineCategory(invoice);
      const invoiceDate = invoice.invoiceDate || invoice.purchaseDate || invoice.date;
      const formattedDate = dayjs(invoiceDate, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD.MM.YYYY']).format('YYYY-MM-DD');

      // Функция парсинга сумм - заменяем запятые на точки и округляем до 2 знаков
      const parseAmount = (value) => {
        if (!value) return 0;
        // Если это строка, заменяем запятую на точку и удаляем пробелы
        const normalized = typeof value === 'string' ? value.replace(',', '.').trim() : value;
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
      };

      // Функция парсинга процентов
      const parseVatRate = (value) => {
        if (!value) return 23.00;
        // Удаляем символ % и пробелы, затем парсим
        const normalized = typeof value === 'string' ? value.replace('%', '').trim() : value;
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? 23.00 : parsed;
      };

      const netAmount = parseAmount(invoice.netAmount);
      const vatAmount = parseAmount(invoice.vatAmount);
      const grossAmount = parseAmount(invoice.grossAmount) || Math.round((netAmount + vatAmount) * 100) / 100;
      const vatRate = parseVatRate(invoice.vatRate);

      console.log(`Client XML: Invoice ${invoice.invoiceId} - net=${netAmount}, vat=${vatAmount}, gross=${grossAmount}, vatRate=${vatRate}%`);

      // Konwersja obrazu do Base64 jeśli jest fileURL
      let imageBase64 = null;
      if (invoice.fileURL) {
        imageBase64 = await imageUrlToBase64(invoice.fileURL);
        if (imageBase64) {
          // Walidacja base64 - sprawdź czy zawiera tylko dozwolone znaki
          const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
          if (!base64Regex.test(imageBase64)) {
            console.error(`✗ Invalid Base64 for invoice ${invoice.invoiceId} - contains invalid characters`);
            console.log('First 200 chars:', imageBase64.substring(0, 200));
            imageBase64 = null;
          } else {
            console.log(`✓ Image converted for invoice ${invoice.invoiceId}, length: ${imageBase64.length}`);
            console.log('First 50 chars:', imageBase64.substring(0, 50));
          }
        } else {
          console.warn(`✗ Failed to convert image for invoice ${invoice.invoiceId}`);
        }
      }

      xml += `        <REJESTR_ZAKUPU_VAT>
            <ID_ZRODLA>${escapeXML(invoice.invoiceId)}</ID_ZRODLA>
            <MODUL>Rejestr Vat</MODUL>
            <TYP>Rejestr zakupu</TYP>
            <REJESTR>ZAKUP</REJESTR>

            <DATA_WYSTAWIENIA>${formattedDate}</DATA_WYSTAWIENIA>
            <DATA_ZAKUPU>${formattedDate}</DATA_ZAKUPU>
            <DATA_WPLYWU>${formattedDate}</DATA_WPLYWU>
            <TERMIN>${dayjs(formattedDate).add(14, 'days').format('YYYY-MM-DD')}</TERMIN>
            <DATA_DATAOBOWIAZKUPODATKOWEGO>${formattedDate}</DATA_DATAOBOWIAZKUPODATKOWEGO>
            <DATA_DATAPRAWAODLICZENIA>${formattedDate}</DATA_DATAPRAWAODLICZENIA>

            <NUMER><![CDATA[${escapeXML(invoice.numerfaktury || invoice.invoiceId)}]]></NUMER>
            <KOREKTA>Nie</KOREKTA>
            <KOREKTA_NUMER></KOREKTA_NUMER>
            <WEWNETRZNA>Nie</WEWNETRZNA>
            <METODA_KASOWA>Nie</METODA_KASOWA>
            <FISKALNA>Nie</FISKALNA>
            <DETALICZNA>Nie</DETALICZNA>
            <EKSPORT>nie</EKSPORT>
            <FINALNY>Nie</FINALNY>
            <PODATNIK_CZYNNY>Tak</PODATNIK_CZYNNY>
            <IDENTYFIKATOR_KSIEGOWY>${String(index + 1).padStart(6, '0')}/${dayjs(formattedDate).format('MM/YYYY')}/ZAKUP/IDKSI</IDENTYFIKATOR_KSIEGOWY>

            <TYP_PODMIOTU>kontrahent</TYP_PODMIOTU>
            <PODMIOT_NIP>${nip}</PODMIOT_NIP>

            <NAZWA1><![CDATA[${escapeXML(invoice.vendorName || contractor?.nazwa || nip).substring(0, 60)}]]></NAZWA1>
            <NAZWA2></NAZWA2>
            <NAZWA3></NAZWA3>
            <NIP_KRAJ></NIP_KRAJ>
            <NIP>${nip}</NIP>
            <KRAJ>Polska</KRAJ>
            <WOJEWODZTWO>${escapeXML(invoice.region || contractor?.wojewodztwo || '')}</WOJEWODZTWO>
            <POWIAT>${escapeXML(invoice.city || contractor?.miasto || '')}</POWIAT>
            <GMINA></GMINA>
            <ULICA>${escapeXML(invoice.address || contractor?.ulica || '')}</ULICA>
            <NR_DOMU></NR_DOMU>
            <NR_LOKALU></NR_LOKALU>
            <MIASTO>${escapeXML(invoice.city || contractor?.miasto || '')}</MIASTO>
            <KOD_POCZTOWY>${escapeXML(invoice.postalCode || contractor?.kodPocztowy || '')}</KOD_POCZTOWY>
            <POCZTA>${escapeXML(invoice.city || contractor?.miasto || '')}</POCZTA>
            <DODATKOWE></DODATKOWE>
            <PESEL></PESEL>
            <ROLNIK>Nie</ROLNIK>

            <TYP_PLATNIKA>pracownik</TYP_PLATNIKA>
            <PLATNIK>${escapeXML(invoice.driverAkronim || '00000')}</PLATNIK>
            <PLATNIK_ID>${generateUUIDFromNIP(invoice.driverId || 'driver')}</PLATNIK_ID>
            <PLATNIK_NIP></PLATNIK_NIP>

            <KATEGORIA>${category}</KATEGORIA>
            <KATEGORIA_ID>${generateUUIDFromNIP(category)}</KATEGORIA_ID>

            <OPIS><![CDATA[${escapeXML(invoice.description || `Zakup paliwa - ${invoice.fuelType || 'diesel'}`)}]]></OPIS>

            <FORMA_PLATNOSCI>przelew</FORMA_PLATNOSCI>
            <FORMA_PLATNOSCI_ID>D18D8E4B-C18E-4AB8-8876-6A67813CA4D8</FORMA_PLATNOSCI_ID>

            <DEKLARACJA_VAT7>${dayjs(formattedDate).format('YYYY-MM')}</DEKLARACJA_VAT7>
            <DEKLARACJA_VATUE>Nie</DEKLARACJA_VATUE>

            <WALUTA>PLN</WALUTA>
            <KURS_WALUTY>NBP</KURS_WALUTY>
            <NOTOWANIE_WALUTY_ILE>1</NOTOWANIE_WALUTY_ILE>
            <NOTOWANIE_WALUTY_ZA_ILE>1</NOTOWANIE_WALUTY_ZA_ILE>
            <DATA_KURSU>${formattedDate}</DATA_KURSU>
            <KURS_DO_KSIEGOWANIA>Nie</KURS_DO_KSIEGOWANIA>
            <KURS_WALUTY_2>NBP</KURS_WALUTY_2>
            <NOTOWANIE_WALUTY_ILE_2>1</NOTOWANIE_WALUTY_ILE_2>
            <NOTOWANIE_WALUTY_ZA_ILE_2>1</NOTOWANIE_WALUTY_ZA_ILE_2>
            <DATA_KURSU_2>${formattedDate}</DATA_KURSU_2>
            <PLATNOSC_VAT_W_PLN>Nie</PLATNOSC_VAT_W_PLN>

            <AKCYZA_NA_WEGIEL>0</AKCYZA_NA_WEGIEL>
            <AKCYZA_NA_WEGIEL_KOLUMNA_KPR>nie księgować</AKCYZA_NA_WEGIEL_KOLUMNA_KPR>

            <JPK_FA>Nie</JPK_FA>
            <MPP>Nie</MPP>
            <NR_KSEF></NR_KSEF>
            <DODATKOWY_OPIS></DODATKOWY_OPIS>

            <POZYCJE>
                <POZYCJA>
                    <LP>1</LP>
                    <KATEGORIA_POS>${category}</KATEGORIA_POS>
                    <KATEGORIA_ID_POS>${generateUUIDFromNIP(category)}</KATEGORIA_ID_POS>
                    <STAWKA_VAT>${Math.round(vatRate)}</STAWKA_VAT>
                    <STATUS_VAT>opodatkowana</STATUS_VAT>
                    <NETTO>${netAmount.toFixed(2)}</NETTO>
                    <VAT>${vatAmount.toFixed(2)}</VAT>
                    <NETTO_SYS>${netAmount.toFixed(2)}</NETTO_SYS>
                    <VAT_SYS>${vatAmount.toFixed(2)}</VAT_SYS>
                    <NETTO_SYS2>${netAmount.toFixed(2)}</NETTO_SYS2>
                    <VAT_SYS2>${vatAmount.toFixed(2)}</VAT_SYS2>
                    <RODZAJ_ZAKUPU>${category === 'PALIWO OS.' ? 'paliwo' : 'usługi'}</RODZAJ_ZAKUPU>
                    <ODLICZENIA_VAT>tak</ODLICZENIA_VAT>
                    <KOLUMNA_KPR>Nie księgować</KOLUMNA_KPR>
                    <KOLUMNA_RYCZALT>Nie księgować</KOLUMNA_RYCZALT>
                    <OPIS_POZ><![CDATA[${escapeXML(invoice.description || `Zakup paliwa - ${invoice.fuelType || 'diesel'}`)}]]></OPIS_POZ>
                    <OPIS_POZ_2></OPIS_POZ_2>
                </POZYCJA>
            </POZYCJE>

            <KWOTY_DODATKOWE/>

            <PLATNOSCI>
                <PLATNOSC>
                    <ID_ZRODLA_PLAT>${generateUUIDFromNIP(`payment-${invoice.invoiceId}`)}</ID_ZRODLA_PLAT>
                    <TERMIN_PLAT>${dayjs(formattedDate).add(14, 'days').format('YYYY-MM-DD')}</TERMIN_PLAT>
                    <FORMA_PLATNOSCI_PLAT>przelew</FORMA_PLATNOSCI_PLAT>
                    <FORMA_PLATNOSCI_ID_PLAT>D18D8E4B-C18E-4AB8-8876-6A67813CA4D8</FORMA_PLATNOSCI_ID_PLAT>
                    <KWOTA_PLAT>${grossAmount.toFixed(2)}</KWOTA_PLAT>
                    <WALUTA_PLAT>PLN</WALUTA_PLAT>
                    <KURS_WALUTY_PLAT>NBP</KURS_WALUTY_PLAT>
                    <NOTOWANIE_WALUTY_ILE_PLAT>1</NOTOWANIE_WALUTY_ILE_PLAT>
                    <NOTOWANIE_WALUTY_ZA_ILE_PLAT>1</NOTOWANIE_WALUTY_ZA_ILE_PLAT>
                    <KWOTA_PLN_PLAT>${grossAmount.toFixed(2)}</KWOTA_PLN_PLAT>
                    <KIERUNEK>rozchód</KIERUNEK>
                    <PODLEGA_ROZLICZENIU>tak</PODLEGA_ROZLICZENIU>
                    <KONTO></KONTO>
                    <NIE_NALICZAJ_ODSETEK>Nie</NIE_NALICZAJ_ODSETEK>
                    <PRZELEW_SEPA>Nie</PRZELEW_SEPA>
                    <DATA_KURSU_PLAT>${formattedDate}</DATA_KURSU_PLAT>
                    <WALUTA_DOK>PLN</WALUTA_DOK>
                    <PLATNOSC_TYP_PODMIOTU>pracownik</PLATNOSC_TYP_PODMIOTU>
                    <PLATNOSC_PODMIOT>${escapeXML(invoice.driverAkronim || '00000')}</PLATNOSC_PODMIOT>
                    <PLATNOSC_PODMIOT_ID>${generateUUIDFromNIP(invoice.driverId || 'driver')}</PLATNOSC_PODMIOT_ID>
                    <PLATNOSC_PODMIOT_NIP></PLATNOSC_PODMIOT_NIP>
                    <PLAT_KATEGORIA>${category}</PLAT_KATEGORIA>
                    <PLAT_KATEGORIA_ID>${generateUUIDFromNIP(category)}</PLAT_KATEGORIA_ID>
                    <PLAT_ELIXIR_O1>Zapłata za ${escapeXML(invoice.numerfaktury || invoice.invoiceId)}</PLAT_ELIXIR_O1>
                    <PLAT_ELIXIR_O2></PLAT_ELIXIR_O2>
                    <PLAT_ELIXIR_O3></PLAT_ELIXIR_O3>
                    <PLAT_ELIXIR_O4></PLAT_ELIXIR_O4>
                    <PLAT_FA_Z_PA>Nie</PLAT_FA_Z_PA>
                    <PLAT_VAN_FA_Z_PA>Nie</PLAT_VAN_FA_Z_PA>
                    <PLAT_SPLIT_PAYMENT>Nie</PLAT_SPLIT_PAYMENT>
                    <PLAT_SPLIT_KWOTA_VAT>${vatAmount.toFixed(2)}</PLAT_SPLIT_KWOTA_VAT>
                    <PLAT_SPLIT_NIP>${nip}</PLAT_SPLIT_NIP>
                    <PLAT_SPLIT_NR_DOKUMENTU>${escapeXML(invoice.numerfaktury || invoice.invoiceId)}</PLAT_SPLIT_NR_DOKUMENTU>
                </PLATNOSC>
            </PLATNOSCI>

`;

      if (imageBase64) {
        console.log(`Adding image for invoice ${invoice.invoiceId}`);
        console.log('Base64 length before adding:', imageBase64.length);
        console.log('Base64 last 50 chars:', imageBase64.substring(imageBase64.length - 50));

        xml += '            <DOKUMENTY_OBD>\n';
        xml += '                <DOKUMENT_OBD>\n';
        xml += '                    <DON_ID_ZRODLA>' + generateUUIDFromNIP(`doc-${invoice.invoiceId}`) + '</DON_ID_ZRODLA>\n';
        xml += '                    <DON_DEFINICJA_DOKUMENTU_ID>8112C042-2A33-11DC-8639-07CE56389593</DON_DEFINICJA_DOKUMENTU_ID>\n';
        xml += '                    <DON_DEFINICJA_DOKUMENTU_SYMBOL>OBD</DON_DEFINICJA_DOKUMENTU_SYMBOL>\n';
        xml += '                    <DON_DEFINICJA_DOKUMENTU_NUMERACJA>@brak/@symbol/@numerS/@rok_kal/@brak</DON_DEFINICJA_DOKUMENTU_NUMERACJA>\n';
        xml += '                    <DON_NUMER_STRING>OBD/@numerS/' + dayjs(formattedDate).format('YYYY') + '</DON_NUMER_STRING>\n';
        xml += '                    <DON_NUMER_NR>' + (index + 1) + '</DON_NUMER_NR>\n';
        xml += '                    <DON_NUMER_PELNY>OBD/' + (index + 1) + '/' + dayjs(formattedDate).format('YYYY') + '</DON_NUMER_PELNY>\n';
        xml += '                    <DON_NUMER_OBCY></DON_NUMER_OBCY>\n';
        xml += '                    <DON_DATA_DOK>' + formattedDate + '</DON_DATA_DOK>\n';
        xml += '                    <DON_TYTUL>' + escapeXML(invoice.numerfaktury || invoice.invoiceId) + '.pdf</DON_TYTUL>\n';
        xml += '                    <DON_TYP>1</DON_TYP>\n';
        xml += '                    <DON_KATALOG_SYMBOL>!Domyślny!</DON_KATALOG_SYMBOL>\n';
        xml += '                    <DON_ORIGINID>0</DON_ORIGINID>\n';
        xml += '                    <DNP_TYP_ZRODLA_DANYCH>baza</DNP_TYP_ZRODLA_DANYCH>\n';
        xml += '                    <DNP_NAZWA_PLIKU>' + escapeXML(invoice.numerfaktury || invoice.invoiceId) + '.pdf</DNP_NAZWA_PLIKU>\n';
        xml += '                    <DNP_SCIEZKA></DNP_SCIEZKA>\n';
        xml += '                    <DNP_WERSJA>1</DNP_WERSJA>\n';
        xml += '                    <DNP_FILE_MODE>plik</DNP_FILE_MODE>\n';
        xml += '                    <DNP_DATA_DOK>' + formattedDate + '</DNP_DATA_DOK>\n';
        xml += '                    <DNP_TYP>1</DNP_TYP>\n';
        xml += '                    <DNP_ARCHIWALNY>0</DNP_ARCHIWALNY>\n';
        xml += '                    <DAB_MAGAZYN_DANYCH>1</DAB_MAGAZYN_DANYCH>\n';
        xml += '                    <DAB_TYP>2</DAB_TYP>\n';
        xml += '                    <DAB_WARTOSC><![CDATA[' + imageBase64 + ']]></DAB_WARTOSC>\n';
        xml += '                </DOKUMENT_OBD>\n';
        xml += '            </DOKUMENTY_OBD>\n';

        console.log('XML length after adding image:', xml.length);

        // Проверяем сколько раз встречается CDATA в секции изображения
        const cdataCount = (xml.match(/<!\[CDATA\[/g) || []).length;
        console.log('Total CDATA count in XML so far:', cdataCount);
      }

      xml += `

            <ATRYBUTY>
                <ATRYBUT>
                    <KOD_ATR>SPARIO_INVOICE_ID</KOD_ATR>
                    <WARTOSC>${escapeXML(invoice.invoiceId)}</WARTOSC>
                </ATRYBUT>
                <ATRYBUT>
                    <KOD_ATR>DRIVER_ID</KOD_ATR>
                    <WARTOSC>${escapeXML(invoice.driverId)}</WARTOSC>
                </ATRYBUT>
                <ATRYBUT>
                    <KOD_ATR>DRIVER_NAME</KOD_ATR>
                    <WARTOSC><![CDATA[${escapeXML(invoice.driverName)}]]></WARTOSC>
                </ATRYBUT>
                ${invoice.registrationNumber ? `<ATRYBUT>
                    <KOD_ATR>VEHICLE_PLATE</KOD_ATR>
                    <WARTOSC>${escapeXML(invoice.registrationNumber)}</WARTOSC>
                </ATRYBUT>` : ''}
                ${invoice.fileURL ? `<ATRYBUT>
                    <KOD_ATR>SCAN_URL</KOD_ATR>
                    <WARTOSC><![CDATA[${escapeXML(invoice.fileURL)}]]></WARTOSC>
                </ATRYBUT>` : ''}
            </ATRYBUTY>
        </REJESTR_ZAKUPU_VAT>

`;
    }

    xml += `    </REJESTRY_ZAKUPU_VAT>
`;
    return xml;
  };

  // Główna funkcja generowania XML
  const generateXML = async (selectedInvoices) => {
    const header = `<?xml version="1.0" encoding="UTF-8"?>
<!--
    Plik XML wygenerowany przez system Spario Panel
    Data generacji: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}
    Okres: ${startDate.format('DD.MM.YYYY')} - ${endDate.format('DD.MM.YYYY')}
    Liczba faktur: ${selectedInvoices.length}
    Liczba kontrahentów: ${new Set(selectedInvoices.map(inv => normalizeNIP(inv.nipseller))).size}
    Format: Comarch ERP Optima 2025.2.1
-->
<ROOT xmlns="http://www.comarch.pl/cdn/optima/offline">

`;

    const footer = `</ROOT>`;

    const contractorsXML = generateContractorsXML(selectedInvoices);
    const invoicesXML = await generateInvoicesXML(selectedInvoices);

    const finalXML = header + contractorsXML + invoicesXML + footer;

    // Walidacja - sprawdź czy nie ma podwójnych CDATA w DAB_WARTOSC
    const dabWartoscMatches = finalXML.match(/<DAB_WARTOSC>.*?<\/DAB_WARTOSC>/gs);
    if (dabWartoscMatches) {
      console.log('Found DAB_WARTOSC sections:', dabWartoscMatches.length);
      dabWartoscMatches.forEach((match, i) => {
        const cdataInSection = (match.match(/<!\[CDATA\[/g) || []).length;
        console.log(`DAB_WARTOSC ${i + 1}: contains ${cdataInSection} CDATA tags`);
        if (cdataInSection > 1) {
          console.error(`ERROR: Multiple CDATA in section ${i + 1}!`);
          console.log('First 500 chars:', match.substring(0, 500));
        }
      });
    }

    return finalXML;
  };

  // Загрузка задач экспорта
  const loadExportTasks = async () => {
    try {
      const tasksSnapshot = await get(ref(db, 'exportTasks'));
      if (tasksSnapshot.exists()) {
        const tasks = [];
        tasksSnapshot.forEach((childSnapshot) => {
          tasks.push({
            ...childSnapshot.val(),
            taskId: childSnapshot.key
          });
        });
        tasks.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setExportTasks(tasks);
      }
    } catch (error) {
      console.error('Error loading export tasks:', error);
    }
  };

  // Функция создания задания на экспорт (вместо handleExport)
  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Wybierz faktury do eksportu');
      return;
    }

    try {
      // Проверяем авторизацию
      const user = currentFirebase.auth.currentUser;
      console.log('Current user:', user);
      console.log('Current project:', currentProject);
      console.log('Auth instance:', currentFirebase.auth);

      if (!user) {
        message.error('Musisz być zalogowany, aby utworzyć zadanie eksportu');
        return;
      }

      message.loading('Tworzenie zadania eksportu...', 0);

      // Используем одно имя функции для всех проектов
      const functionName = 'createComarchExportTask';

      // Выводим в логи проект и ссылку на функцию
      const functionUrl = `https://${functionsRegion}-${currentFirebase.app.options.projectId}.cloudfunctions.net/${functionName}`;
      console.log('='.repeat(80));
      console.log('CALLING CLOUD FUNCTION');
      console.log('='.repeat(80));
      console.log('Project ID:', currentProject);
      console.log('Firebase Project:', currentFirebase.app.options.projectId);
      console.log('Function Name:', functionName);
      console.log('Function Region:', functionsRegion);
      console.log('Function URL:', functionUrl);
      console.log('='.repeat(80));

      const createTask = httpsCallable(functions, functionName);
      const exportType = dataSource === 'archive' ? 'archive' : 'normal';
      const result = await createTask({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        filterStatus,
        selectedInvoiceKeys: selectedRowKeys,
        projectId: currentProject, // Передаем ID проекта в функцию
        exportType // 'normal' или 'archive'
      });

      message.destroy();

      if (result.data.success) {
        message.success(`Zadanie utworzone! ID: ${result.data.taskId}`);
        setShowTasksModal(true);
      }
    } catch (error) {
      console.error('Error creating export task:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      message.destroy();
      message.error(`Błąd podczas tworzenia zadania eksportu: ${error.message || error.code || 'Unknown error'}`);
    }
  };

  // OLD: Funkcja do eksportu XML (теперь не используется, но оставляем для совместимости)
  const handleExportOld = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Wybierz faktury do eksportu');
      return;
    }

    try {
      // Filtruj faktury na podstawie wybranych kluczy
      const selectedInvoices = invoices.filter(inv => selectedRowKeys.includes(inv.key));

      message.loading(`Generowanie XML... (ładowanie skanów dla ${selectedInvoices.length} faktur)`, 0);
      const xmlContent = await generateXML(selectedInvoices);
      message.destroy();

      const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      const timestamp = dayjs().format('YYYYMMDDHHmmss');
      const fileName = `Comarch_Optima_${startDate.format('YYYY-MM-DD')}_${endDate.format('YYYY-MM-DD')}_${selectedInvoices.length}faktur_${timestamp}.xml`;

      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Plik XML został wygenerowany: ${fileName}`);
    } catch (error) {
      console.error('Error generating XML:', error);
      message.destroy();
      message.error('Błąd podczas generowania pliku XML');
    }
  };

  // Funkcja do podglądu XML w konsoli (dla testowania)
  const handlePreview = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Wybierz faktury do podglądu');
      return;
    }

    try {
      // Filtruj faktury na podstawie wybranych kluczy
      const selectedInvoices = invoices.filter(inv => selectedRowKeys.includes(inv.key));

      message.loading(`Generowanie podglądu... (ładowanie skanów dla ${selectedInvoices.length} faktur)`, 0);
      const xmlContent = await generateXML(selectedInvoices);
      message.destroy();

      console.log('='.repeat(80));
      console.log('COMARCH ERP OPTIMA XML - PODGLĄD');
      console.log('='.repeat(80));
      console.log(`Liczba faktur: ${selectedInvoices.length}`);
      console.log(`Liczba kontrahentów: ${new Set(selectedInvoices.map(inv => normalizeNIP(inv.nipseller))).size}`);
      console.log(`Okres: ${startDate.format('DD.MM.YYYY')} - ${endDate.format('DD.MM.YYYY')}`);
      console.log('='.repeat(80));
      console.log('\nPrzykład struktury pierwszej faktury:');
      console.log('-'.repeat(80));

      // Wyciągnij pierwszą fakturę z XML
      const firstInvoiceMatch = xmlContent.match(/<REJESTR_ZAKUPU_VAT>[\s\S]*?<\/REJESTR_ZAKUPU_VAT>/);
      if (firstInvoiceMatch) {
        console.log(firstInvoiceMatch[0].substring(0, 3000) + '\n...\n');
      }

      console.log('-'.repeat(80));
      console.log('\nPełny XML zapisany w zmiennej: window.__COMARCH_XML__');
      console.log('Użyj: console.log(window.__COMARCH_XML__) aby zobaczyć pełną wersję');
      console.log('='.repeat(80));

      // Przykładowe dane z pierwszej faktury
      if (selectedInvoices.length > 0) {
        const invoice = selectedInvoices[0];
        console.log('\nDane pierwszej wybranej faktury:');
        console.log(JSON.stringify({
          invoiceId: invoice.invoiceId,
          numerfaktury: invoice.numerfaktury,
          nipseller: invoice.nipseller,
          vendorName: invoice.vendorName,
          date: invoice.date,
          netAmount: invoice.netAmount,
          vatAmount: invoice.vatAmount,
          grossAmount: invoice.grossAmount,
          vatRate: invoice.vatRate,
          fuelType: invoice.fuelType,
          driverId: invoice.driverId,
          driverName: invoice.driverName,
          fileURL: invoice.fileURL ? 'TAK' : 'NIE'
        }, null, 2));
      }

      message.success('XML wyświetlony w konsoli przeglądarki (F12)');
    } catch (error) {
      console.error('Error generating XML preview:', error);
      message.destroy();
      message.error('Błąd podczas generowania podglądu XML');
    }
  };

  // Kolonki dla tabeli podglądu
  const columns = [
    {
      title: 'Data',
      dataIndex: 'displayDate',
      key: 'date',
      width: 110,
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (date) => <Text style={{ color: '#1d1d1f', fontSize: '13px' }}>{date}</Text>
    },
    {
      title: 'Numer faktury',
      dataIndex: 'numerfaktury',
      key: 'invoice',
      width: 180,
      ellipsis: true,
      render: (text) => <Text copyable style={{ color: '#1d1d1f', fontSize: '13px' }}>{text}</Text>
    },
    {
      title: 'NIP',
      dataIndex: 'nipseller',
      key: 'nip',
      width: 140,
      render: (nip) => <Text style={{ color: '#86868b', fontSize: '13px', fontFamily: 'monospace' }}>{normalizeNIP(nip)}</Text>
    },
    {
      title: 'Typ',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => <Text style={{ color: '#86868b', fontSize: '13px' }}>{type}</Text>
    },
    {
      title: 'Netto',
      dataIndex: 'netAmount',
      key: 'net',
      width: 120,
      align: 'right',
      sorter: (a, b) => {
        const parseAmt = (v) => parseFloat(typeof v === 'string' ? v.replace(',', '.') : v) || 0;
        return parseAmt(a.netAmount) - parseAmt(b.netAmount);
      },
      render: (val) => {
        const normalized = typeof val === 'string' ? val.replace(',', '.') : val;
        const num = Math.round(parseFloat(normalized || 0) * 100) / 100;
        return <Text style={{ color: '#1d1d1f', fontSize: '13px' }}>{num.toFixed(2)} zł</Text>;
      }
    },
    {
      title: 'VAT',
      dataIndex: 'vatAmount',
      key: 'vat',
      width: 110,
      align: 'right',
      sorter: (a, b) => {
        const parseAmt = (v) => parseFloat(typeof v === 'string' ? v.replace(',', '.') : v) || 0;
        return parseAmt(a.vatAmount) - parseAmt(b.vatAmount);
      },
      render: (val) => {
        const normalized = typeof val === 'string' ? val.replace(',', '.') : val;
        const num = Math.round(parseFloat(normalized || 0) * 100) / 100;
        return <Text style={{ color: '#86868b', fontSize: '13px' }}>{num.toFixed(2)} zł</Text>;
      }
    },
    {
      title: 'Brutto',
      dataIndex: 'grossAmount',
      key: 'gross',
      width: 130,
      align: 'right',
      sorter: (a, b) => {
        const parseAmt = (v) => parseFloat(typeof v === 'string' ? v.replace(',', '.') : v) || 0;
        return parseAmt(a.grossAmount) - parseAmt(b.grossAmount);
      },
      render: (val) => {
        const normalized = typeof val === 'string' ? val.replace(',', '.') : val;
        const num = Math.round(parseFloat(normalized || 0) * 100) / 100;
        return <Text style={{ color: '#0071e3', fontSize: '14px', fontWeight: 600 }}>{num.toFixed(2)} zł</Text>;
      }
    },
  ];

  // Рендер статуса задачи
  const renderTaskStatus = (task) => {
    if (task.status === 'pending') {
      return <Tag icon={<ClockCircleOutlined />} color="default">Oczekuje</Tag>;
    } else if (task.status === 'processing') {
      return <Tag icon={<SyncOutlined spin />} color="processing">W trakcie</Tag>;
    } else if (task.status === 'completed') {
      return <Tag icon={<CheckCircleOutlined />} color="success">Gotowe</Tag>;
    } else if (task.status === 'failed') {
      return <Tag icon={<WarningOutlined />} color="error">Błąd</Tag>;
    }
    return <Tag>Nieznany</Tag>;
  };

  return (
    <div style={{
      margin: '-24px -24px',
      padding: '32px',
      minHeight: 'calc(100vh - 64px)'
    }}>
      <div style={{
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        <Title level={2} style={{
          marginBottom: '8px',
          fontWeight: 600,
          color: '#1d1d1f',
          fontSize: '40px',
          letterSpacing: '-0.5px'
        }}>
          Eksport XML
        </Title>
        <Text style={{
          fontSize: '17px',
          color: '#86868b',
          fontWeight: 400,
          letterSpacing: '-0.2px'
        }}>
          Comarch ERP Optima 2025.2.1
        </Text>
        <div style={{ marginTop: '16px' }}>
          <Button
            type="default"
            icon={<FileTextOutlined />}
            onClick={() => setShowTasksModal(true)}
            style={{
              borderRadius: '10px',
              fontWeight: 500
            }}
          >
            Zadania eksportu ({exportTasks.length})
          </Button>
        </div>
      </div>

      {/* Modal ze списком задач */}
      <Modal
        title="Zadania eksportu XML"
        open={showTasksModal}
        onCancel={() => setShowTasksModal(false)}
        footer={null}
        width={1200}
      >
        <Table
          dataSource={exportTasks}
          rowKey="taskId"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1100 }}
          columns={[
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 120,
              fixed: 'left',
              render: (_, task) => renderTaskStatus(task)
            },
            {
              title: 'Okres',
              key: 'period',
              width: 180,
              render: (_, task) => (
                <Text style={{ fontSize: '13px' }}>
                  {dayjs(task.params?.startDate).format('DD.MM.YYYY')} - {dayjs(task.params?.endDate).format('DD.MM.YYYY')}
                </Text>
              )
            },
            {
              title: 'Faktury',
              key: 'invoiceCount',
              width: 70,
              align: 'center',
              render: (_, task) => <Text strong>{task.params?.invoiceCount || 0}</Text>
            },
            {
              title: 'Postęp',
              key: 'progress',
              width: 320,
              render: (_, task) => (
                <div style={{ minWidth: '300px' }}>
                  <Progress
                    percent={task.progress || 0}
                    size="small"
                    status={task.progress === 100 ? 'success' : 'active'}
                  />
                  {task.currentStep && (
                    <Text
                      style={{
                        fontSize: '11px',
                        color: '#86868b',
                        marginTop: '4px',
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={task.currentStep}
                    >
                      {task.currentStep}
                    </Text>
                  )}
                </div>
              )
            },
            {
              title: 'Data utworzenia',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 140,
              render: (timestamp) => (
                <Text style={{ fontSize: '13px', color: '#86868b' }}>
                  {dayjs(timestamp).format('DD.MM.YYYY HH:mm')}
                </Text>
              )
            },
            {
              title: 'Akcje',
              key: 'actions',
              width: 120,
              fixed: 'right',
              render: (_, task) => (
                <Space direction="vertical" size="small">
                  {task.status === 'completed' && task.downloadUrl && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        window.open(task.downloadUrl, '_blank');
                      }}
                      block
                    >
                      Pobierz
                    </Button>
                  )}
                  {task.status === 'failed' && task.error && (
                    <Text type="danger" style={{ fontSize: '11px', wordBreak: 'break-word' }}>
                      {task.error}
                    </Text>
                  )}
                </Space>
              )
            }
          ]}
        />
      </Modal>


      {/* Wybór źródła danych */}
      <Card
        bordered={false}
        style={{
          marginBottom: '20px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text style={{ fontSize: '13px', fontWeight: 500, color: '#86868b' }}>Źródło danych</Text>
          <Select
            value={dataSource}
            onChange={setDataSource}
            style={{ width: '300px' }}
            size="large"
          >
            <Option value="realtime">Faktury bieżące (Realtime Database)</Option>
            <Option value="archive">Faktury archiwalne (Firestore)</Option>
          </Select>
        </Space>
      </Card>

      {/* Filtry */}
      <Card
        bordered={false}
        style={{
          marginBottom: '20px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text style={{ fontSize: '13px', fontWeight: 500, color: '#86868b' }}>Data od</Text>
              <DatePicker
                value={startDate}
                onChange={(date) => {
                  if (date) {
                    setStartDate(date);
                  }
                }}
                format="DD.MM.YYYY"
                style={{
                  width: '100%',
                  borderRadius: '10px',
                  border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
                placeholder="Data od"
                allowClear={false}
                size="large"
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text style={{ fontSize: '13px', fontWeight: 500, color: '#86868b' }}>Data do</Text>
              <DatePicker
                value={endDate}
                onChange={(date) => {
                  if (date) {
                    setEndDate(date);
                  }
                }}
                format="DD.MM.YYYY"
                style={{
                  width: '100%',
                  borderRadius: '10px',
                  border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
                placeholder="Data do"
                allowClear={false}
                size="large"
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text style={{ fontSize: '13px', fontWeight: 500, color: '#86868b' }}>Status faktury</Text>
              <Select
                value={filterStatus}
                onChange={setFilterStatus}
                style={{
                  width: '100%'
                }}
                size="large"
              >
                <Option value="all">Wszystkie statusy</Option>
                <Option value="zaakceptowany">Zaakceptowane</Option>
                <Option value="odrzucony">Odrzucone</Option>
                <Option value="W trakcie weryfikacji">W trakcie weryfikacji</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text style={{ opacity: 0, fontSize: '13px' }}>.</Text>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => dataSource === 'archive' ? loadArchiveInvoices() : loadInvoices()}
                loading={loading}
                block
                size="large"
                style={{
                  height: '40px',
                  borderRadius: '10px',
                  background: '#0071e3',
                  border: 'none',
                  fontWeight: 500,
                  boxShadow: '0 4px 12px rgba(0, 113, 227, 0.2)'
                }}
              >
                Szukaj
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Szybki wybór okresu */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <Space wrap size="small">
          <Button
            type="text"
            onClick={() => {
              setStartDate(dayjs().startOf('month'));
              setEndDate(dayjs().endOf('month'));
            }}
            style={{
              borderRadius: '20px',
              fontSize: '13px',
              padding: '4px 16px',
              height: 'auto',
              color: '#1d1d1f',
              background: 'rgba(0, 0, 0, 0.04)'
            }}
          >
            Ten miesiąc
          </Button>
          <Button
            type="text"
            onClick={() => {
              setStartDate(dayjs().subtract(1, 'month').startOf('month'));
              setEndDate(dayjs().subtract(1, 'month').endOf('month'));
            }}
            style={{
              borderRadius: '20px',
              fontSize: '13px',
              padding: '4px 16px',
              height: 'auto',
              color: '#1d1d1f',
              background: 'rgba(0, 0, 0, 0.04)'
            }}
          >
            Poprzedni miesiąc
          </Button>
          <Button
            type="text"
            onClick={() => {
              setStartDate(dayjs().subtract(7, 'days'));
              setEndDate(dayjs());
            }}
            style={{
              borderRadius: '20px',
              fontSize: '13px',
              padding: '4px 16px',
              height: 'auto',
              color: '#1d1d1f',
              background: 'rgba(0, 0, 0, 0.04)'
            }}
          >
            Ostatnie 7 dni
          </Button>
          <Button
            type="text"
            onClick={() => {
              setStartDate(dayjs().subtract(30, 'days'));
              setEndDate(dayjs());
            }}
            style={{
              borderRadius: '20px',
              fontSize: '13px',
              padding: '4px 16px',
              height: 'auto',
              color: '#1d1d1f',
              background: 'rgba(0, 0, 0, 0.04)'
            }}
          >
            Ostatnie 30 dni
          </Button>
          <Button
            type="text"
            onClick={() => {
              setStartDate(dayjs().startOf('year'));
              setEndDate(dayjs().endOf('year'));
            }}
            style={{
              borderRadius: '20px',
              fontSize: '13px',
              padding: '4px 16px',
              height: 'auto',
              color: '#1d1d1f',
              background: 'rgba(0, 0, 0, 0.04)'
            }}
          >
            Cały rok
          </Button>
        </Space>
      </div>

      {/* Statystyki */}
      <Row gutter={[12, 12]} style={{ marginBottom: '32px' }}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card
            bordered={false}
            style={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s ease',
              cursor: 'default'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ fontSize: '13px', color: '#86868b', marginBottom: '8px', fontWeight: 500 }}>
              Wszystkie faktury
            </div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-1px' }}>
              {statistics.total}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card
            bordered={false}
            style={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s ease',
              cursor: 'default'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ fontSize: '13px', color: '#86868b', marginBottom: '8px', fontWeight: 500 }}>
              Wybrane do eksportu
            </div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#0071e3', letterSpacing: '-1px' }}>
              {selectedRowKeys.length}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card
            bordered={false}
            style={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s ease',
              cursor: 'default'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ fontSize: '13px', color: '#86868b', marginBottom: '8px', fontWeight: 500 }}>
              Zaakceptowane
            </div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#30d158', letterSpacing: '-1px' }}>
              {statistics.accepted}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card
            bordered={false}
            style={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s ease',
              cursor: 'default'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ fontSize: '13px', color: '#86868b', marginBottom: '8px', fontWeight: 500 }}>
              Odrzucone
            </div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#ff3b30', letterSpacing: '-1px' }}>
              {statistics.rejected}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card
            bordered={false}
            style={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)',
              transition: 'transform 0.2s ease',
              cursor: 'default'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ fontSize: '13px', color: '#86868b', marginBottom: '8px', fontWeight: 500 }}>
              Kontrahenci
            </div>
            <div style={{ fontSize: '32px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-1px' }}>
              {statistics.contractors}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Akcje eksportu */}
      {invoices.length > 0 && (
        <Card
          bordered={false}
          style={{
            marginBottom: '20px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space direction="vertical" size={2}>
                <Text strong style={{ fontSize: '17px', color: '#1d1d1f', fontWeight: 600 }}>
                  {selectedRowKeys.length > 0
                    ? `Wybrano ${selectedRowKeys.length} ${selectedRowKeys.length === 1 ? 'fakturę' : 'faktur'}`
                    : 'Zaznacz faktury do eksportu'}
                </Text>
                <Text style={{ fontSize: '13px', color: '#86868b' }}>
                  Załadowano {invoices.length} {invoices.length === 1 ? 'fakturę' : 'faktur'} z wybranego okresu
                </Text>
              </Space>
            </Col>
            <Col>
              <Space size="small">
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={() => setSelectedRowKeys(invoices.map(inv => inv.key))}
                  disabled={invoices.length === 0}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Zaznacz wszystkie
                </Button>
                <Button
                  icon={<CloseCircleOutlined />}
                  onClick={() => setSelectedRowKeys([])}
                  disabled={selectedRowKeys.length === 0}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Odznacz
                </Button>
                <Button
                  type="default"
                  icon={<FileTextOutlined />}
                  onClick={handlePreview}
                  disabled={selectedRowKeys.length === 0}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Podgląd
                </Button>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={handleExport}
                  disabled={selectedRowKeys.length === 0}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    background: '#0071e3',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    boxShadow: '0 4px 12px rgba(0, 113, 227, 0.3)',
                    paddingLeft: '24px',
                    paddingRight: '24px'
                  }}
                >
                  Utwórz zadanie ({selectedRowKeys.length})
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Tabela faktur */}
      {invoices.length === 0 && !loading ? (
        <Card
          bordered={false}
          style={{
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)'
          }}
        >
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <FileTextOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#1d1d1f', fontWeight: 600 }}>Brak faktur do wyświetlenia</Title>
            <Text style={{ color: '#86868b', fontSize: '14px' }}>
              Wybierz okres i status faktur, a następnie kliknij "Szukaj"
            </Text>
          </div>
        </Card>
      ) : (
        <div style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <Table
            columns={columns}
            dataSource={invoices}
            loading={loading}
            title={() => (
              <div style={{
                fontWeight: 600,
                fontSize: '17px',
                color: '#1d1d1f',
                padding: '16px 24px',
                marginBottom: '-16px'
              }}>
                Lista faktur ({invoices.length})
              </div>
            )}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => {
                setSelectedRowKeys(newSelectedRowKeys);
              },
              selections: [
                Table.SELECTION_ALL,
                Table.SELECTION_INVERT,
                Table.SELECTION_NONE,
              ],
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => (
                <span style={{ color: '#86868b', fontSize: '13px' }}>
                  Łącznie: {total} {total === 1 ? 'faktura' : 'faktur'}
                </span>
              ),
              position: ['bottomCenter']
            }}
            scroll={{ x: 1000 }}
            rowKey="key"
            size="middle"
            bordered={false}
            style={{
              background: 'transparent'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ExportXMLComarch;
